/**
 * pluginEvaluator.ts
 * Évalue à runtime le code TypeScript/JSX d'un plugin utilisateur généré par l'IA.
 *
 * Supporte deux formats :
 *   A) NOUVEAU (simple) : export default function App({ config }) + export const flexihubConfig
 *   B) ANCIEN (legacy)  : export default { metadata, PreviewComponent, ... } (PluginInterface complet)
 *
 * Dans les deux cas, retourne un objet compatible PluginInterface prêt pour pluginRegistry.
 */

import React from 'react';
import * as Babel from '@babel/standalone';
import { createPortal } from 'react-dom';
import { generateUniformPlaceholder } from './PlaceholderGenerator';
import axios from 'axios';
import QRCode from 'qrcode';
import jsQR from 'jsqr';
import parse from 'html-react-parser';
import { Rnd } from 'react-rnd';
import * as LucideReact from 'lucide-react';
import YouTube from 'react-youtube';
import JSZip from 'jszip';
import * as ReactIconsFi from 'react-icons/fi';
import * as ReactIconsFa from 'react-icons/fa';
import * as ReactIconsMd from 'react-icons/md';
import * as ReactIconsBs from 'react-icons/bs';
import * as ReactIconsAi from 'react-icons/ai';

// ── Map des modules disponibles ───────────────────────────────────────────────
const RUNTIME_MODULES: Record<string, any> = {
  'react': { __esModule: true, default: React, ...React },
  'react-dom': { __esModule: true, default: { createPortal }, createPortal },
  'react-dom/client': { __esModule: true },
  'qrcode': { __esModule: true, default: QRCode },
  'jsqr': { __esModule: true, default: jsQR },
  'axios': { __esModule: true, default: axios, get: axios.get, post: axios.post, put: axios.put, delete: axios.delete },
  'html-react-parser': { __esModule: true, default: parse },
  'react-rnd': { __esModule: true, Rnd },
  'lucide-react': { __esModule: true, ...LucideReact },
  'react-youtube': { __esModule: true, default: YouTube },
  'jszip': { __esModule: true, default: JSZip, JSZip },
  'react-icons/fi': { __esModule: true, ...ReactIconsFi },
  'react-icons/fa': { __esModule: true, ...ReactIconsFa },
  'react-icons/md': { __esModule: true, ...ReactIconsMd },
  'react-icons/bs': { __esModule: true, ...ReactIconsBs },
  'react-icons/ai': { __esModule: true, ...ReactIconsAi },
  '../types/PluginInterface': { __esModule: true },
  '../types/plugin': { __esModule: true },
  '../utils/PlaceholderGenerator': { __esModule: true, generateUniformPlaceholder },
  './PlaceholderGenerator': { __esModule: true, generateUniformPlaceholder },
};

// ── Nettoyage des artefacts LLM AVANT tout traitement ─────────────────────────
/**
 * Supprime les artefacts courants introduits par les LLMs (API ou local) :
 * BOM, balises markdown résiduelles, entités HTML, caractères invisibles.
 * Appelé en premier dans transpile() avant toute autre transformation.
 */
function stripLLMArtifacts(code: string): string {
  // 1. BOM Unicode (souvent ajouté par Claude/GPT-4 en mode stream)
  code = code.replace(/^\uFEFF/, '');

  // 2. Caractères zero-width et espaces insécables invisibles
  //    \u200B zero-width space, \u200C zero-width non-joiner, \u200D zero-width joiner
  //    \u202F narrow no-break space, \u00A0 no-break space, \uFEFF BOM (déjà retiré)
  code = code.replace(/[\u200B\u200C\u200D\u202F\u00A0\uFEFF]/g, (ch) => {
    // \u00A0 dans du code peut être un espace légitime entre tokens → remplacer par espace normal
    return ch === '\u00A0' ? ' ' : '';
  });

  // 3. Balises markdown résiduelles en début/fin (``` typescript, ```tsx, ``` seul)
  //    Cas : l'extraction regex a raté le début ou la réponse commence par la balise
  code = code.replace(/^```(?:typescript|tsx?|javascript|jsx?|js)?\s*\n?/i, '');
  code = code.replace(/\n?```\s*$/i, '');

  // 4. Entités HTML encodées (certains LLMs échappent le HTML dans leur réponse)
  code = code.replace(/&lt;/g, '<')
             .replace(/&gt;/g, '>')
             .replace(/&amp;/g, '&')
             .replace(/&quot;/g, '"')
             .replace(/&#39;/g, "'")
             .replace(/&apos;/g, "'");

  // 5. Séquence d'échappement littérale \uXXXX en début de fichier
  //    (quand le LLM écrit la séquence comme texte au lieu du caractère réel)
  //    Ex: "\u0069mport" → "import"
  code = code.replace(/\\u([0-9a-fA-F]{4})/g, (_, hex) =>
    String.fromCharCode(parseInt(hex, 16))
  );

  return code.trim();
}

// ── Pré-traitement : corrections courantes du code généré par LLM ─────────────
function preprocessPluginCode(code: string): string {
  // 0. Normaliser les fins de ligne Windows → Unix
  code = code.replace(/\r\n/g, '\n');

  // 1. Insérer les virgules manquantes entre propriétés d'objets
  //    Patterns couverts : string/bool/number/}/)/]/identifier
  //    On boucle jusqu'à stabilisation (pour les virgules en cascade)
  const RE_COMMA = /((?:'[^'\n]*'|"[^"\n]*"|`[^`\n]*`|true|false|null|undefined|[\d.]+|\)|\]|\}|\w+))([ \t]*)(\n)([ \t]+)([a-zA-Z_$][a-zA-Z0-9_$]*[ \t]*[:?])/g;
  const INSERT_COMMA = (_m: string, val: string, trail: string, nl: string, indent: string, prop: string) =>
    val + ',' + trail + nl + indent + prop;

  let prev = '';
  while (prev !== code) {
    prev = code;
    code = code.replace(RE_COMMA, INSERT_COMMA);
  }

  // 2. Supprimer les décorateurs non supportés (@Decorator avant class/function)
  code = code.replace(/^@\w[\w.]*(\([^)]*\))?\s*\n/gm, '');
  return code;
}

/**
 * Corrige le pattern fréquent : le LLM génère `const [activeTab, setActiveTab]`
 * mais utilise `tab` (non déclaré) dans le JSX.
 * Appliqué AVANT la transpilation Babel (le code compile mais crashe à runtime).
 */
function fixUndeclaredTabVariable(code: string): string {
  // Si `tab` est déjà déclaré → rien à faire
  if (/\b(?:const|let|var)\s+tab\b/.test(code)) return code;
  // Si `tab` n'apparaît pas comme identifiant → rien à faire
  if (!/\btab\b/.test(code)) return code;
  // Chercher un état dont le nom contient "tab" / "Tab" (ex: activeTab, currentTab, selectedTab)
  const m = code.match(/const\s+\[([a-zA-Z_$][a-zA-Z0-9_$]*[Tt]ab[a-zA-Z0-9_$]*|[Tt]ab[a-zA-Z0-9_$]+)\s*,/);
  if (!m || m[1] === 'tab') return code;
  const actual = m[1]; // ex: "activeTab"
  const fixed = code.replace(/\btab\b/g, actual);
  console.info(`[pluginEvaluator] Fix sémantique: 'tab' → '${actual}'`);
  return fixed;
}

// ── Transpilation Babel ───────────────────────────────────────────────────────
const BABEL_PRESETS = [
  ['react', { runtime: 'classic' }],
  ['typescript', { allExtensions: true, isTSX: true }],
  ['env', { targets: { chrome: '90' }, modules: 'commonjs', useBuiltIns: false }],
];

async function transpile(code: string): Promise<string | null> {
  // 0. Nettoyer les artefacts LLM (BOM, backticks résiduels, entités HTML…)
  code = stripLLMArtifacts(code);
  // Corrections sémantiques appliquées inconditionnellement (avant Babel)
  code = fixUndeclaredTabVariable(code);

  // Premier essai : code tel quel
  try {
    const result = Babel.transform(code, {
      presets: BABEL_PRESETS,
      filename: 'plugin.tsx',
      sourceType: 'module',
    });
    if (result?.code) return result.code;
  } catch (firstErr) {
    // Deuxième essai : après pré-traitement (corrections virgules manquantes etc.)
    try {
      const fixed = preprocessPluginCode(code);
      const result = Babel.transform(fixed, {
        presets: BABEL_PRESETS,
        filename: 'plugin.tsx',
        sourceType: 'module',
      });
      if (result?.code) {
        console.info('[pluginEvaluator] Code corrigé automatiquement avant transpilation');
        return result.code;
      }
    } catch (secondErr) {
      // Les deux passes ont échoué — log le deuxième pour faciliter le diagnostic
      console.warn('[pluginEvaluator] Preprocessing n\'a pas résolu le problème:', secondErr);
      throw firstErr;
    }
  }
  return null;
}

// ── Exécution dans un sandbox minimal ────────────────────────────────────────
function execute(transformed: string): { exports: any } {
  const safeRequire = (name: string): any => {
    if (name in RUNTIME_MODULES) return RUNTIME_MODULES[name];
    if (/\.(css|scss|less|svg|png|jpg|gif|webp)$/.test(name)) return {};
    if (name.startsWith('react-icons/')) return { __esModule: true };
    console.warn(`[pluginEvaluator] Module inconnu: "${name}"`);
    return { __esModule: true };
  };
  const mod: { exports: any } = { exports: {} };
  // `config` en paramètre de new Function = fallback pour les composants helpers
  // qui référencent config.X depuis la portée externe sans le recevoir en prop.
  // Les composants qui déclarent ({ config }) en props recouvrent ce fallback.
  // eslint-disable-next-line no-new-func
  new Function('require', 'module', 'exports', 'React', 'process', 'config', transformed)(
    safeRequire, mod, mod.exports, React, { env: { NODE_ENV: 'production' } }, {},
  );
  return mod;
}

// ── Error boundary générique pour composants plugin ──────────────────────────
class _ConfigErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; msg: string }
> {
  constructor(props: any) { super(props); this.state = { hasError: false, msg: '' }; }
  static getDerivedStateFromError(err: Error) { return { hasError: true, msg: err.message }; }
  componentDidCatch(err: Error) { console.warn('[pluginEvaluator] Erreur runtime plugin:', err.message); }
  render() {
    if (this.state.hasError) {
      return React.createElement('div', {
        style: { padding: '10px 14px', color: '#c0392b', background: '#fdf3f2', borderRadius: 6, fontSize: 12, border: '1px solid #f5c6cb' },
      }, `⚠ Erreur plugin : ${this.state.msg}`);
    }
    return this.props.children as React.ReactElement;
  }
}

function wrapConfigComponent(
  Component: React.ComponentType<{ config: any; onConfigChange: (c: any) => void }>,
): React.ComponentType<{ config: any; onConfigChange: (c: any) => void }> {
  return function SafeConfigComponent(props: { config: any; onConfigChange: (c: any) => void }) {
    return React.createElement(_ConfigErrorBoundary, null, React.createElement(Component, props));
  };
}

// ── Wrap format A : composant simple + flexihubConfig ─────────────────────────
function wrapSimpleComponent(
  Component: React.ComponentType<{ config: any }>,
  flexihubConfig: any,
  ConfigComponent?: React.ComponentType<{ config: any; onConfigChange: (c: any) => void }> | null,
): any {
  const defConfig = flexihubConfig?.defaults ?? {};
  const pluginType = flexihubConfig?.id ?? `user-plugin-${Date.now()}`;
  const name = flexihubConfig?.name ?? 'Plugin';
  const description = flexihubConfig?.description ?? '';
  const icon = flexihubConfig?.icon ?? '🔧';
  const category = flexihubConfig?.category ?? 'utility';

  // PreviewComponent : le composant de l'IA, avec merge des defaults + error boundary
  const PreviewComponent: React.FC<{ config: any }> = ({ config }) =>
    React.createElement(_ConfigErrorBoundary, null,
      React.createElement(Component, { config: { ...defConfig, ...config } }));
  PreviewComponent.displayName = name;

  return {
    metadata: {
      id: pluginType,
      name,
      description,
      version: '1.0.0',
      author: 'FlexiHub AI',
      category,
      icon,
      tags: ['user', 'custom', 'ai'],
    },
    documentationForAI: {
      description,
      usage: `Plugin personnalisé "${name}"`,
      configSchema: Object.fromEntries(
        Object.entries(defConfig).map(([k, v]) => [k, typeof v])
      ),
      exampleConfig: defConfig,
    },
    defaultConfig: defConfig,
    // ConfigComponent personnalisé si exporté — enveloppé dans un error boundary
    ...(ConfigComponent ? { ConfigComponent: wrapConfigComponent(ConfigComponent) } : {}),
    PreviewComponent,
    generateHTML: (config: any) => generateUniformPlaceholder({
      title: name,
      description,
      icon,
      category,
      config: { ...defConfig, ...config, id: config.id || `${pluginType}-${Date.now()}` },
      pluginId: config.id || `${pluginType}-${Date.now()}`,
      pluginType,
    }),
  };
}

// ── Point d'entrée principal ──────────────────────────────────────────────────

/**
 * Évalue le code TypeScript/JSX d'un plugin.
 * Retourne un objet PluginInterface-compatible.
 * Lance une exception avec le message d'erreur détaillé en cas d'échec
 * (erreur Babel, erreur runtime, ou export non reconnu).
 */
export async function evaluatePluginCode(code: string): Promise<any> {
  if (!code?.trim()) throw new Error('Code vide ou absent');

  let transformed: string | null;
  try {
    transformed = await transpile(code);
  } catch (transpileErr: any) {
    // Extraire le message Babel lisible (ex: "Unexpected token (42:8)")
    const msg = transpileErr?.message || String(transpileErr);
    throw new Error(`Erreur de transpilation Babel : ${msg}`);
  }

  if (!transformed) {
    throw new Error('Babel n\'a produit aucun code (sortie vide)');
  }

  let mod: { exports: any };
  try {
    mod = execute(transformed);
  } catch (runtimeErr: any) {
    const msg = runtimeErr?.message || String(runtimeErr);
    throw new Error(`Erreur d'exécution du plugin : ${msg}`);
  }

  const exported = mod.exports;
  const defaultExport = exported?.default ?? exported;
  const flexihubConfig = exported?.flexihubConfig ?? null;
  const ConfigComponent = typeof exported?.ConfigComponent === 'function' ? exported.ConfigComponent : null;

  // ── Format A : composant fonction (nouvelle approche recommandée) ──────────
  if (typeof defaultExport === 'function' && flexihubConfig) {
    console.log('[pluginEvaluator] Format A détecté (composant + flexihubConfig' + (ConfigComponent ? ' + ConfigComponent' : '') + ')');
    return wrapSimpleComponent(defaultExport as React.ComponentType<{ config: any }>, flexihubConfig, ConfigComponent);
  }

  // ── Format A sans flexihubConfig : composant seul, config depuis le commentaire ──
  if (typeof defaultExport === 'function' && !flexihubConfig) {
    console.log('[pluginEvaluator] Format A sans flexihubConfig — extraction depuis @flexihub:config=');
    const configMatch = code.match(/\/\/\s*@flexihub:config=(\{.*?\})/);
    const defConfig = configMatch ? JSON.parse(configMatch[1]) : {};
    const nameMatch = code.match(/(?:function|const)\s+([A-Z][A-Za-z0-9]+)/);
    const extractedName = nameMatch?.[1]?.replace(/([A-Z])/g, ' $1').trim() ?? 'Plugin';
    return wrapSimpleComponent(defaultExport as React.ComponentType<{ config: any }>, {
      id: `user-plugin-${Date.now()}-modular`,
      name: extractedName,
      description: '',
      icon: '🔧',
      category: 'utility',
      defaults: defConfig,
    }, ConfigComponent);
  }

  // ── Format B : PluginInterface complet (ancien format) ────────────────────
  if (defaultExport?.metadata?.id && defaultExport?.PreviewComponent) {
    console.log('[pluginEvaluator] Format B détecté (PluginInterface complet)');
    return defaultExport;
  }

  // Export non reconnu — message diagnostic précis
  const exportType = typeof defaultExport;
  const hasFlexihubConfig = !!flexihubConfig;
  const hasDefault = exportType === 'function' || exportType === 'object';
  if (!hasDefault) {
    throw new Error(
      `Le code ne contient pas d'export default. Assure-toi d'avoir "export default function MonComposant({ config }) { ... }" et "export const flexihubConfig = { id, name, ... }".`
    );
  }
  if (!hasFlexihubConfig) {
    throw new Error(
      `export default trouvé mais "export const flexihubConfig" est absent ou mal formé. Ajoute : export const flexihubConfig = { id: '...', name: '...', defaults: {} };`
    );
  }
  throw new Error(
    `Export non reconnu (type: ${exportType}). Le composant doit être une fonction React et flexihubConfig doit avoir les champs id, name, defaults.`
  );
}
